import SeasonCard from "./SeasonOverview";
import React, { useMemo } from "react";
import ScrollTabs from "./ScrollTabs";
import PerformanceChart from "./PerformanceChart";
import { PlayerAvatar, positionLabel } from "./LineupPitch";
import { averageRating, gameForFixture, mainCompetition, positionIndicators, positionTag, recentForm } from "./playerModel";
import { chooseSeasonView, buildStatisticsView, explainDifference } from "./statSource";
import ShotExplorer from "../football-intelligence/ShotExplorer";
import HeatmapExplorer from "../football-intelligence/HeatmapExplorer";
import CompareView from "../football-intelligence/CompareView";
import ComparePicker from "../football-intelligence/ComparePicker";
import { formatMetric } from "../football-intelligence/fiData";
import "../football-intelligence/football-intelligence.css";
import "../football-intelligence/fi-extra.css";
import "./football-app.css";

const SAMPLE_SIZES = [5, 10, 20, 50, 1000];
const show = (v, d = 0) => (v === null || v === undefined ? "—" : typeof v === "number" && !Number.isInteger(v) ? v.toFixed(d || 1) : String(v));
const ratingClass = (r) => (r >= 7 ? "" : r >= 6.4 ? "mid" : "low");
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("en-GB", { day: "2-digit", month: "short" }) : "—");

export const Rating = ({ value }) => (value === null || value === undefined ? null : <span className={`fa-rating ${ratingClass(value)}`}>{Number(value).toFixed(1)}</span>);

function MatchRow({ g, onOpen }) {
  const result = g.result || "";
  return (
    <div className="fa-matchrow">
      <span className="d">{fmtDate(g.date)}</span>
      <span className="o">
        <b>{g.home === false ? "@ " : ""}{g.opponent}</b>
        <span className="fa-sub">{g.competition}{g.minutes !== null && g.minutes !== undefined ? ` · ${g.minutes}'` : ""}</span>
      </span>
      <span className="r">
        {g.goals > 0 && <span title="Goals">⚽{g.goals}</span>}
        {g.assists > 0 && <span title="Assists" className="fa-sub">A{g.assists}</span>}
        {result && <span className={`fa-res ${result}`} title={g.score}>{g.score || result}</span>}
        <Rating value={g.rating} />
      </span>
    </div>
  );
}


function Indicators({ pos, competitions }) {
  const main = mainCompetition(competitions);
  const ind = positionIndicators(pos, main);
  if (!ind.rows.length) return null;
  const max = Math.max(...ind.rows.map((r) => r.per90 ?? 0), 0.01);
  return (
    <section className="fa-card" aria-label="Performance indicators">
      <h3>Performance indicators</h3>
      <span className="fa-scope">{ind.scope} · {ind.minutes} min</span>
      {ind.rows.map((r) => (
        <div className="fa-ind" key={r.key}>
          <span>{r.label}</span>
          <span className="track" aria-hidden="true">{r.per90 !== null && <i style={{ width: `${Math.max(4, (r.per90 / max) * 100)}%` }} />}</span>
          <b>{r.per90 !== null ? r.per90.toFixed(2) : "—"}<small className="fa-sub" style={{ display: "block", fontWeight: 400 }}>{r.total} total</small></b>
        </div>
      ))}
      <p className="fa-note">Per 90 minutes in the main competition above. These are raw rates for this player, not rankings: no percentile or league comparison is shown because a validated comparison group does not exist yet.</p>
    </section>
  );
}

function IntelFilters({ intel, filters, onFilter }) {
  const d = intel.data;
  if (!d || d.status === "UNAVAILABLE") return null;
  return (
    <div className="fa-filters">
      <label>Competition
        <select aria-label="Competition" value={filters.competition || ""} onChange={(e) => onFilter("competition", e.target.value)}>
          <option value="">All cached competitions</option>
          {(d.competitions || []).map((c) => <option key={c} value={c}>{d.competition_names?.[c] || c}</option>)}
        </select>
      </label>
      <label>Sample
        <select aria-label="Sample" value={filters.last_n || 10} onChange={(e) => onFilter("last_n", e.target.value)}>
          {SAMPLE_SIZES.map((n) => <option key={n} value={n}>{n === 1000 ? "Full cached season" : `Last ${n} matches`}</option>)}
        </select>
      </label>
    </div>
  );
}

function StatisticsTab({ seasonCompetitions, seasonView, intel, filters, onFilter, analyticsAllowed, compare }) {
  const scopeLabel = [`Last ${filters.last_n || 10}`, filters.competition ? intel.data?.competition_names?.[filters.competition] || filters.competition : "all competitions"].join(" · ");
  const view = buildStatisticsView({ pitch: intel.data, seasonCompetitions, scopeLabel });
  const sampleGoals = intel.data?.metrics?.goals?.value ?? null;
  const diff = view.source === "pitchapi" && !seasonView.source ? explainDifference({ seasonGoals: seasonView.total?.goals ?? seasonView.rows[0]?.goals ?? null, sampleGoals, sampleMatches: intel.data?.sample_size, sampleRange: intel.data?.date_range }) : "";

  return (
    <>
      <section className="fa-card">
        <div className="fa-srcrow">
          {view.sourceLabel && <span className="fa-chip">Source · {view.sourceLabel}</span>}
          {view.sampleNote && <span className="fa-sub">{view.sampleNote}</span>}
        </div>
        {analyticsAllowed && <IntelFilters intel={intel} filters={filters} onFilter={onFilter} />}
        {view.warnings.map((w) => <div className="fa-warn" role="note" key={w}>{w}</div>)}
        {intel.loading && <div className="fa-skeleton" role="status" aria-label="Loading statistics" style={{ height: 80 }} />}
        {!view.sections.length && !intel.loading && <div className="fa-empty">No recorded statistics for this player.</div>}
        {view.sections.map((sec) => (
          <div key={sec.id} style={{ marginTop: 14 }}>
            <h3 className="fa-title" style={{ marginBottom: 2 }}>{sec.title}</h3>
            {sec.rows.map((r) => (
              <div className="fa-statrow" key={r.key} title={r.hint || undefined}>
                <span>{r.label}{r.sample ? <small>{r.sample} matches</small> : null}</span>
                <b>{r.value === null ? "—" : r.percent ? `${r.value.toFixed(1)}%` : formatMetric(r.key, r.value)}</b>
                <span className="p90">{r.per90 !== null ? `${r.per90.toFixed(2)} /90` : ""}</span>
              </div>
            ))}
          </div>
        ))}
        {diff && <p className="fa-note">{diff}</p>}
        {view.source === "pitchapi" && <p className="fa-note">PitchAPI · historical actuals, not live tracking · recorded provider data.</p>}
      </section>
      {analyticsAllowed && intel.data && intel.data.status !== "UNAVAILABLE" && (
        <section className="fa-card fa-darkembed" id="compare" aria-label="Compare">
          <h3>Compare</h3>
          <div className="fi-controls"><ComparePicker kind="player" value={compare.id} onChange={compare.set} exclude={intel.data.profile?.id} /></div>
          {intel.comparison ? <CompareView a={intel.data} b={intel.comparison} kind="player" per90 /> : <p className="fa-note">Choose a player to compare over the same competition and sample. Raw values only, not league-adjusted.</p>}
        </section>
      )}
    </>
  );
}

/**
 * One coherent player experience. Public data comes from API-Football; the
 * private PitchAPI layers (detailed statistics, shot map, heatmap, compare)
 * appear only for administrators with a verified identity mapping.
 */
export default function PlayerProfileView({
  summary, games, providerFixtureId, fixtureId, teams, tab, onTab, isAdmin, analyticsAllowed = isAdmin, intel, filters, onFilter, compare, renderProps, onNavigate, clubHref,
}) {
  const player = summary?.player || {};
  const competitions = useMemo(() => summary?.season_stats?.competitions || [], [summary]);
  const seasonView = useMemo(() => {
    return chooseSeasonView(competitions, analyticsAllowed ? intel.data?.season_summary : null);
  }, [competitions, analyticsAllowed, intel.data?.season_summary]);
  const recent = useMemo(() => recentForm(games, 5), [games]);
  const allMatches = useMemo(() => recentForm(games, 40), [games]);
  const avg = averageRating(games.slice(0, 5));
  const thisMatch = gameForFixture(games, providerFixtureId);
  const teamName = summary?.team?.name || "";
  const teamId = games[0]?.team_id ?? null;
  const number = player.number ?? competitions.find((c) => c?.games?.number)?.games?.number ?? null;
  const pos = player.position || competitions[0]?.games?.position || "";
  const intelOk = intel.data && intel.data.status === "AVAILABLE";
  const shots = intelOk ? intel.data.shots || [] : [];
  const heat = intelOk ? intel.data.heatmaps || [] : [];
  const matchOptions = intelOk ? (intel.data.matches || []).map((r) => ({ fixture_id: r.fixture_id, name: r.fixture?.name || String(r.fixture_id) })) : [];
  const matchName = (id) => matchOptions.find((m) => String(m.fixture_id) === String(id))?.name || "";

  const tabs = [
    { id: "overview", label: "Overview" },
    { id: "statistics", label: "Statistics" },
    { id: "matches", label: "Matches" },
    ...(shots.length ? [{ id: "shots", label: "Shot map" }] : []),
    ...(heat.length ? [{ id: "heatmap", label: "Heatmap" }] : []),
    { id: "props", label: "Props" },
  ];
  const current = tabs.some((t) => t.id === tab) ? tab : "overview";

  return (
    <div className="fa">
      <header className="fa-header">
        <div className="fa-wrap">
          <div className="fa-header-top">
            <button type="button" className="fa-back" onClick={() => onNavigate(fixtureId ? `/fixture/${fixtureId}?tab=lineups` : "/")} aria-label="Back to match">←</button>
            <div className="fa-header-meta"><strong>Player</strong><span>{positionLabel(positionTag(pos)) || pos}</span></div>
          </div>
          <div className="fa-phead">
            <PlayerAvatar player={{ id: player.id, photo: player.photo, number }} large />
            <div style={{ minWidth: 0 }}>
              <h1>{player.name || "Player"}</h1>
              <div className="fa-sub">
                {teamId && teamName ? <a href={clubHref(teamId, teamName)} onClick={(e) => { e.preventDefault(); onNavigate(clubHref(teamId, teamName)); }}>{teamName}</a> : teamName}
                {pos ? ` · ${pos}` : ""}{number ? ` · #${number}` : ""}
              </div>
              {avg && <div className="fa-sub" style={{ marginTop: 4 }}>Average rating {avg.value.toFixed(2)} · last {avg.n} · API-Football</div>}
            </div>
          </div>
        </div>
      </header>
      <ScrollTabs tabs={tabs} value={current} onChange={onTab} label="Player sections" idPrefix="fa-player" />
      <div className="fa-wrap" role="tabpanel" id={`fa-player-panel-${current}`} aria-labelledby={`fa-player-tab-${current}`}>
        {current === "overview" && (
          <>
            {thisMatch && (
              <section className="fa-card" aria-label="This match">
                <h3>This match <span className="fa-sub">· {thisMatch.team} {thisMatch.score ? thisMatch.score : "vs"} {thisMatch.opponent}</span></h3>
                <div className="fa-tiles">
                  <div className="fa-tile"><b>{show(thisMatch.minutes)}</b><span>Minutes</span></div>
                  <div className="fa-tile"><b>{show(thisMatch.goals)}</b><span>Goals</span></div>
                  <div className="fa-tile"><b>{show(thisMatch.assists)}</b><span>Assists</span></div>
                  <div className="fa-tile"><b>{show(thisMatch.shots)}</b><span>Shots</span></div>
                  {thisMatch.rating && <div className="fa-tile"><b>{Number(thisMatch.rating).toFixed(1)}</b><span>Rating</span></div>}
                </div>
              </section>
            )}
            {analyticsAllowed && intelOk && <section className="fa-card" aria-label="Recorded player overview">
              <h3>Recorded performance</h3>
              <p className="fa-scope">{intel.data.sample_size} recorded matches · {intel.data.date_range?.map(d => new Date(d).toLocaleDateString("en-GB")).join(" – ")}. This sample is separate from season totals.</p>
              <div className="fa-tiles">{[
                ["goals", "Goals"], ["assists", "Assists"], ["total_shots", "Shots"], ["expected_goals", "xG"],
                ["expected_assists", "Expected assists"], ["passes_attempted", "Passes"], ["accurate_passes", "Passes completed"],
                ["chances_created", "Chances created"], ["tackles", "Tackles"], ["interceptions", "Interceptions"],
                ["touches", "Touches"], ["progressive_passes", "Progressive passes"],
              ].filter(([key]) => Number.isFinite(intel.data.metrics?.[key]?.value)).map(([key, label]) =>
                <div className="fa-tile" key={key}><b>{Number(intel.data.metrics[key].value.toFixed(2))}</b><span>{label}</span></div>
              )}</div>
              <button type="button" className="fa-btn" onClick={() => onTab("statistics")}>Full player statistics</button>
            </section>}
            <div className="fa-twocol">
              <SeasonCard seasonView={seasonView} />
              <section className="fa-card" aria-label="Recent form">
                <h3>Recent form</h3>
                {recent.length ? recent.map((g) => <MatchRow key={g.fixtureId} g={g} />) : <div className="fa-empty">No recent matches recorded.</div>}
                <button type="button" className="fa-btn" style={{ width: "100%", marginTop: 8 }} onClick={() => onTab("matches")}>All matches</button>
              </section>
            </div>
            <Indicators pos={pos} competitions={competitions} />
            {summary?.match_stats && (summary.match_stats.yc_per90 !== undefined || summary.match_stats.fouls_per90 !== undefined) && (
              <section className="fa-card" aria-label="Discipline">
                <h3>Discipline</h3>
                <span className="fa-scope">Current league · {summary.match_stats.minutes} min</span>
                <div className="fa-tiles">
                  <div className="fa-tile"><b>{show(summary.match_stats.yc_per90, 2)}</b><span>Yellows / 90</span></div>
                  <div className="fa-tile"><b>{show(summary.match_stats.fouls_per90, 2)}</b><span>Fouls / 90</span></div>
                </div>
              </section>
            )}
          </>
        )}
        {current === "statistics" && (
          <StatisticsTab seasonCompetitions={competitions} seasonView={seasonView} intel={intel} filters={filters} onFilter={onFilter} analyticsAllowed={analyticsAllowed} compare={compare} />
        )}
        {current === "matches" && (
          <>
            <section className="fa-card" aria-label="Performance chart">
              <h3>Performance</h3>
              <PerformanceChart games={games} />
            </section>
            <section className="fa-card" aria-label="Match log">
              <h3>Match log</h3>
              {allMatches.length ? allMatches.map((g) => <MatchRow key={g.fixtureId} g={g} />) : <div className="fa-empty">No matches recorded.</div>}
              <p className="fa-note">Every competition is listed with its own label; nothing here is combined into a single total.</p>
            </section>
          </>
        )}
        {current === "shots" && (
          <section className="fa-card fa-darkembed">
            <h3>Shot map</h3>
            {analyticsAllowed && <IntelFilters intel={intel} filters={filters} onFilter={onFilter} />}
            <ShotExplorer shots={shots} matchOptions={matchOptions} heading="" />
          </section>
        )}
        {current === "heatmap" && (
          <section className="fa-card fa-darkembed">
            <h3>Heatmap</h3>
            <p className="fa-note" style={{ marginTop: 0 }}>Recorded action density from provider event data. This is not touches or continuous player tracking.</p>
            {analyticsAllowed && <IntelFilters intel={intel} filters={filters} onFilter={onFilter} />}
            <HeatmapExplorer heatmaps={heat} matchName={matchName} sampleSize={intel.data?.sample_size} />
          </section>
        )}
        {current === "props" && <div className="fa-legacy">{renderProps()}</div>}
      </div>
    </div>
  );
}
